import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { TriangleAlert } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { EventCapacityField } from '@/components/events/event-capacity-field'
import { type useEventCapacity } from '@/components/events/use-event-capacity'

export function isLoweringCapacityBelowSold({
  capacity,
  currentCapacity,
  enabled,
  soldCount,
}: {
  capacity: string
  currentCapacity?: number | null
  enabled: boolean
  soldCount?: number
}) {
  if (!enabled || !isCapacityBelowSold(capacity, soldCount)) return false

  const nextCapacity = Number(capacity)
  return currentCapacity === null || currentCapacity === undefined || nextCapacity < currentCapacity
}

export function CapacityBelowSoldDialog({
  soldCount,
  capacity,
  pending,
  onCancel,
  onConfirm,
}: {
  soldCount: number
  capacity: string
  pending: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Capacity is below seats sold</DialogTitle>
          <DialogDescription>
            Sold ({soldCount}) already exceeds this capacity — sales stop, existing registrations
            are untouched.
          </DialogDescription>
        </DialogHeader>
        <Alert variant="warning">
          {soldCount} seats are sold; capacity will be {capacity}. New sales stay closed until seats
          sold drops below {capacity}.
        </Alert>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" disabled={pending} onClick={onConfirm}>
            Save capacity
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function EventCapacityDetail({
  eventCapacity,
  soldCount = 0,
}: {
  eventCapacity: ReturnType<typeof useEventCapacity>
  soldCount?: number
}) {
  return (
    <div className="grid gap-2 px-4 py-3 sm:grid-cols-[148px_minmax(0,1fr)] sm:items-start">
      <span className="pt-1.5 text-caption text-muted-foreground">Event capacity</span>
      <div className="min-w-0">
        <EventCapacityField
          enabled={eventCapacity.enabled}
          capacity={eventCapacity.capacity}
          soldCount={soldCount}
          onEnabledChange={eventCapacity.setEnabled}
          onCapacityChange={eventCapacity.setCapacity}
        />
        {eventCapacity.enabled && isCapacityBelowSold(eventCapacity.capacity, soldCount) && (
          <Alert variant="warning" className="mt-3 flex gap-2">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <div>
              <p className="font-medium">Capacity is below seats sold</p>
              <p className="mt-0.5 text-caption">
                {soldCount} seats are sold; capacity is now {eventCapacity.capacity}. Existing
                orders are kept. New sales stay closed until seats sold drops below{' '}
                {eventCapacity.capacity}.
              </p>
            </div>
          </Alert>
        )}
      </div>
    </div>
  )
}

function isCapacityBelowSold(capacity: string, soldCount = 0) {
  const parsed = Number(capacity)
  return Number.isInteger(parsed) && parsed > 0 && parsed < soldCount
}
